import { createContext, useContext, useEffect, useMemo, type PropsWithChildren } from 'react';
import { AudioEngine } from '../../engine/audio/audio-engine';
import { reportClientError } from '../../services/appwrite/report-client-error';
import { usePreferences } from '../stores/preferences';

const AudioContext = createContext<AudioEngine | null>(null);

interface AudioProviderProps extends PropsWithChildren {
  /** Motor alternativo para pruebas. */
  engine?: AudioEngine;
}

/**
 * Da acceso al motor de audio y le traslada los volúmenes elegidos.
 *
 * Ningún componente construye un `Howl`: piden `playEffect` y el motor decide.
 * Así el volumen por canal, el silencio y la limpieza viven en un solo sitio.
 */
export function AudioProvider({ children, engine }: AudioProviderProps) {
  const instance = useMemo(
    () =>
      engine ??
      new AudioEngine({
        onError: (message, details) =>
          reportClientError({ source: 'web-app', operation: 'audio-engine', level: 'warning', message, details }),
      }),
    [engine],
  );
  const volumes = usePreferences((state) => state.volumes);
  const muted = usePreferences((state) => state.muted);

  useEffect(() => {
    for (const [channel, value] of Object.entries(volumes)) {
      instance.setVolume(channel as Parameters<AudioEngine['setVolume']>[0], value);
    }
  }, [instance, volumes]);

  useEffect(() => {
    instance.setMuted(muted);
  }, [instance, muted]);

  useEffect(() => () => instance.dispose(), [instance]);

  return <AudioContext.Provider value={instance}>{children}</AudioContext.Provider>;
}

export function useAudio(): AudioEngine {
  const engine = useContext(AudioContext);
  if (!engine) throw new Error('useAudio debe utilizarse dentro de AudioProvider.');
  return engine;
}
